import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RouteIcon from '@mui/icons-material/Route';

const STATUS_STYLES = {
  PENDING: { label: 'Scheduled', color: '#38bdf8', background: 'rgba(14, 116, 144, 0.2)' },
  IN_PROGRESS: { label: 'In progress', color: '#4ade80', background: 'rgba(22, 101, 52, 0.22)' },
  PAUSED: { label: 'Paused', color: '#fbbf24', background: 'rgba(146, 64, 14, 0.24)' },
};

export default function ActiveMissionPanel({
  mission,
  error = '',
  controlAction = '',
  onPause,
  onResume,
  onCancel,
  sx = {},
}) {
  if (!mission && !error) return null;

  const progress = mission?.progress || {};
  const status = STATUS_STYLES[mission?.status] || {
    label: mission?.status || 'Unavailable',
    color: '#f87171',
    background: 'rgba(127, 29, 29, 0.24)',
  };
  const busy = Boolean(controlAction);

  return (
    <Paper
      elevation={10}
      sx={{
        position: 'absolute',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        zIndex: 80,
        width: 300,
        maxWidth: 'calc(100% - 24px)',
        p: 1.5,
        color: '#e2e8f0',
        background: 'linear-gradient(145deg, rgba(15,23,42,0.96), rgba(17,34,48,0.96))',
        border: '1px solid rgba(56,189,248,0.3)',
        borderRadius: 2,
        backdropFilter: 'blur(12px)',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <RouteIcon sx={{ color: '#38bdf8', mt: 0.25 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800 }}>
            {mission?.missionName || 'Mission state unavailable'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {mission?.missionType === 'temporary-point' ? 'Temporary point mission' : 'Scheduled mission'}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={status.label}
          sx={{
            height: 22,
            color: status.color,
            bgcolor: status.background,
            fontWeight: 700,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Box>

      {mission && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.25, mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
              {mission.status === 'PENDING'
                ? 'Scheduled for robot'
                : progress.currentTask?.name || 'Updating task'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', ml: 1 }}>
              {progress.completed || 0}/{progress.total || 0}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, Number(progress.percentage) || 0))}
            sx={{
              height: 6,
              borderRadius: 4,
              bgcolor: 'rgba(148,163,184,0.2)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: mission.status === 'PAUSED'
                  ? '#fbbf24'
                  : mission.status === 'PENDING'
                    ? '#38bdf8'
                    : '#22c55e',
              },
            }}
          />
        </>
      )}

      {error && (
        <Typography variant="caption" sx={{ display: 'block', color: '#fca5a5', mt: 1 }}>
          {error}
        </Typography>
      )}

      {mission && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
          {mission.status === 'IN_PROGRESS' && (
            <Tooltip title="Pause mission">
              <span>
                <IconButton
                  size="small"
                  aria-label="Pause mission"
                  onClick={onPause}
                  disabled={busy}
                  sx={{ color: '#fbbf24' }}
                >
                  {controlAction === 'pause'
                    ? <CircularProgress size={20} color="inherit" />
                    : <PauseCircleOutlineIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {mission.status === 'PAUSED' && (
            <Tooltip title="Resume mission">
              <span>
                <IconButton
                  size="small"
                  aria-label="Resume mission"
                  onClick={onResume}
                  disabled={busy}
                  sx={{ color: '#4ade80' }}
                >
                  {controlAction === 'resume'
                    ? <CircularProgress size={20} color="inherit" />
                    : <PlayCircleOutlineIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Cancel mission">
            <span>
              <IconButton
                size="small"
                aria-label="Cancel mission"
                onClick={onCancel}
                disabled={busy}
                sx={{ color: '#f87171' }}
              >
                {controlAction === 'cancel'
                  ? <CircularProgress size={20} color="inherit" />
                  : <CancelOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
}
